import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Checkbox, Chip, TextField, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CloseIcon from '@material-ui/icons/Close'
import { useSelector } from 'react-redux'
import { Modal, Button, Input, Picker } from '../../root/components/common'
import { CustomSwitch2 } from '../../root/components/common/switch'
import {
    WEBHOOK_TYPES,
    REQUEST_METHODS,
    REASONS_ORDERED,
    reasonToGroup,
    LEGACY_VERSION
} from './constants'
import { useWebhookVersions } from '../hooks/use-webhooks'
import HeadersEditor, {
    headersToRows,
    extractVersion,
    rowsToHeaders,
    validateHeaderRows,
    isHeadersSectionDirty
} from './headers-editor'

const useStyles = makeStyles(theme => ({
    root: {
        maxHeight: '90vh',
        overflowY: 'auto'
        // display: 'flex',
        // flexDirection: 'column'
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        marginBottom: 4
    },
    subtitle: {
        fontSize: 13,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        marginBottom: 24
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20
    },
    row: {
        display: 'flex',
        gap: 16
    },
    field: {
        flex: 1
    },
    fieldLabel: {
        fontSize: 12,
        fontFamily: theme.fontFamily,
        color: theme.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 6
    },
    pickerWrapper: {
        border: theme.border,
        borderRadius: theme.borderRadius,
        padding: '6px 12px',
        background: theme.tertiary,
        minHeight: 46,
        display: 'flex',
        alignItems: 'center'
    },
    pickerError: {
        outline: `1px solid ${theme.error}`
    },
    pickerPlaceholder: {
        color: theme.textSecondary,
        fontStyle: 'italic',
        fontSize: 13
    },
    enabledRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        border: theme.border,
        borderRadius: theme.borderRadius,
        background: theme.lightBlueGrey
    },
    enabledLabel: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    enabledTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily
    },
    enabledHint: {
        fontSize: 12,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily
    },
    section: {
        borderTop: theme.border,
        paddingTop: 20
    },
    fieldError: {
        fontSize: 12,
        color: theme.error,
        fontFamily: theme.fontFamily,
        marginTop: 4,
        paddingLeft: 4
    },
    actions: {
        marginTop: 28,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12
    },
    reasonsAutocomplete: {
        '& .MuiOutlinedInput-root': {
            padding: '4px 8px',
            background: theme.tertiary,
            borderRadius: theme.borderRadius,
            minHeight: 46,
            alignItems: 'center'
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none'
        },
        '& .MuiAutocomplete-input': {
            padding: '6px 4px !important',
            fontSize: 13,
            color: theme.textPrimary,
            fontFamily: theme.fontFamily
        }
    },
    reasonsAutocompleteError: {
        '& .MuiOutlinedInput-root': {
            outline: `1px solid ${theme.error}`
        }
    },
    chip: {
        borderRadius: 7,
        background: theme.background,
        border: `1px solid ${theme.grayLight}`,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily,
        fontSize: 12,
        fontWeight: 500,
        height: 28,
        paddingLeft: 4,
        margin: 3,
        '& .MuiChip-label': {
            paddingLeft: 6,
            paddingRight: 6
        },
        '& .MuiChip-deleteIcon': {
            color: theme.textSecondary,
            width: 14,
            height: 14,
            marginRight: 4,
            transition: 'color 120ms ease',
            '&:hover': {
                color: theme.error
            }
        }
    },
    optionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '2px 0'
    },
    optionLabel: {
        fontSize: 13,
        color: theme.textPrimary,
        fontFamily: theme.fontFamily
    },
    optionCheckbox: {
        padding: 0,
        marginRight: 2,
        color: theme.textSecondary,
        '&.Mui-checked': {
            color: theme.primary
        }
    },
    groupHeader: {
        fontSize: 10,
        fontWeight: 700,
        color: theme.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        padding: '10px 16px 6px',
        background: theme.lightBlueGrey,
        borderBottom: `1px solid ${theme.grayLight}`,
        position: 'sticky',
        top: 0,
        zIndex: 1
    },
    groupItems: {
        padding: '4px 0'
    },
    autocompletePaper: {
        background: theme.background,
        border: theme.border,
        borderRadius: theme.borderRadius,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
    },
    autocompleteListbox: {
        padding: 0,
        maxHeight: 360,
        '& .MuiAutocomplete-option': {
            padding: '8px 16px',
            fontSize: 13,
            fontFamily: theme.fontFamily,
            '&[aria-selected="true"]': {
                background: theme.lightBlueGrey
            },
            '&[data-focus="true"]': {
                background: theme.tertiary
            }
        }
    },
    reasonsCounter: {
        marginTop: 6,
        fontSize: 11,
        color: theme.textSecondary,
        fontFamily: theme.fontFamily,
        letterSpacing: 0.3
    }
}))

const defaultValues = {
    name: '',
    description: '',
    endpoint: '',
    request_method: REQUEST_METHODS.post,
    reasons: [],
    is_enabled: false
}

function buildOptions(map, labelMap) {
    const result = {}
    for (const key of Object.values(map)) {
        result[key] = labelMap?.[key] ?? key
    }
    return result
}

function renderSingleValue(selected) {
    if (!selected) return ''
    if (typeof selected === 'string') return selected
    return selected.name ?? selected.id ?? ''
}

export default function WebhookEditorModal({
    open,
    mode,
    webhook,
    onClose,
    onSubmit,
    isSaving,
    canUpdate,
    billingConfirmation
}) {
    const classes = useStyles()
    const locale = useSelector(
        state => state.main.locale.integrations.webhooksTab
    )

    const initialValues = useMemo(() => {
        if (mode === 'edit' && webhook) {
            return {
                name: webhook.name ?? '',
                description: webhook.description ?? '',
                endpoint: webhook.endpoint ?? '',
                request_method: webhook.request_method ?? REQUEST_METHODS.post,
                reasons: Array.isArray(webhook.reasons) ? webhook.reasons : [],
                is_enabled: Boolean(webhook.is_enabled)
            }
        }
        return defaultValues
    }, [mode, webhook])

    const initialHeaderRows = useMemo(
        () =>
            mode === 'edit' && webhook ? headersToRows(webhook.headers) : [],
        [mode, webhook]
    )

    const { data: versionData } = useWebhookVersions()
    const versions = versionData?.versions ?? []
    const latestVersion = versionData?.latest

    const initialVersion = useMemo(() => {
        if (mode === 'edit' && webhook) {
            // No stored header means webhooks-api is already dispatching this webhook as v1.0.
            // Showing the latest version instead would silently change the customer's payload
            // format the next time anyone saves an unrelated field.
            return extractVersion(webhook.headers) ?? LEGACY_VERSION
        }
        return latestVersion
    }, [mode, webhook, latestVersion])

    const [values, setValues] = useState(initialValues)
    const [headerRows, setHeaderRows] = useState(initialHeaderRows)
    // null means "the user has not picked one", so a late GET /webhooks/versions can still supply
    // the default. Resetting this in the effect below would clobber whatever they had typed.
    const [version, setVersion] = useState(null)
    const [errors, setErrors] = useState({})

    const effectiveVersion = version ?? initialVersion

    useEffect(() => {
        if (open) {
            setValues(initialValues)
            setHeaderRows(initialHeaderRows)
            setVersion(null)
            setErrors({})
        }
    }, [open, initialValues, initialHeaderRows])

    const setField = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }))
        setErrors(prev => {
            if (!prev[field]) return prev
            const rest = { ...prev }
            delete rest[field]
            return rest
        })
    }

    const methodOptions = useMemo(
        () => buildOptions(REQUEST_METHODS, locale.methodLabels),
        [locale]
    )

    const reasonLabel = option => locale.reasonLabels?.[option] ?? option
    const groupLabel = group => locale.reasonGroupLabels?.[group] ?? group

    const validate = () => {
        const next = {}
        if (!values.name.trim()) next.name = locale.validation.nameRequired
        if (!values.endpoint.trim()) {
            next.endpoint = locale.validation.endpointRequired
        }
        if (!values.reasons || values.reasons.length === 0) {
            next.reasons = locale.validation.reasonsRequired
        }
        const headerErr = validateHeaderRows(headerRows, {
            ...locale.validation
        })
        if (headerErr) next.headers = headerErr

        setErrors(next)
        return Object.keys(next).length === 0
    }

    const buildSubmitBody = () => {
        // A version change is a header change, so it has to mark the section dirty on its own.
        const sectionDirty =
            isHeadersSectionDirty(headerRows, initialHeaderRows) ||
            effectiveVersion !== initialVersion

        if (mode === 'create') {
            const body = {
                name: values.name.trim(),
                endpoint: values.endpoint.trim(),
                webhook_type: WEBHOOK_TYPES.traplinked,
                request_method: values.request_method,
                reasons: values.reasons,
                is_enabled: values.is_enabled
            }
            if (values.description && values.description.trim()) {
                body.description = values.description.trim()
            }
            // headers is always sent now — it always carries at least the version header.
            body.headers = rowsToHeaders(headerRows, effectiveVersion)
            return body
        }

        const body = {}
        if (values.name.trim() !== (webhook.name ?? '')) {
            body.name = values.name.trim()
        }
        const trimmedDesc = (values.description ?? '').trim()
        const initialDesc = webhook.description ?? ''
        if (trimmedDesc !== initialDesc) {
            body.description = trimmedDesc.length > 0 ? trimmedDesc : null
        }
        if (values.endpoint.trim() !== (webhook.endpoint ?? '')) {
            body.endpoint = values.endpoint.trim()
        }
        if (values.request_method !== webhook.request_method) {
            body.request_method = values.request_method
        }
        const initialReasons = Array.isArray(webhook.reasons)
            ? webhook.reasons
            : []
        if (!sameReasons(initialReasons, values.reasons)) {
            body.reasons = values.reasons
        }
        if (Boolean(webhook.is_enabled) !== Boolean(values.is_enabled)) {
            body.is_enabled = values.is_enabled
        }
        if (sectionDirty) {
            // Never null: the version header is mandatory, so the section is never empty.
            body.headers = rowsToHeaders(headerRows, effectiveVersion)
        }
        return body
    }

    const handleSave = () => {
        if (!validate()) return

        const body = buildSubmitBody()
        const willEnableNow =
            (mode === 'create' && body.is_enabled) ||
            (mode === 'edit' &&
                body.is_enabled === true &&
                !webhook?.is_enabled)

        const submit = () => onSubmit(body)
        if (willEnableNow && billingConfirmation) {
            billingConfirmation.requestConfirmation(submit)
        } else {
            submit()
        }
    }

    const title = mode === 'edit' ? locale.editWebhook : locale.createWebhook

    return (
        <Modal open={open} setOpen={onClose} size="lg" className={classes.root}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>
                {locale.editorSubtitle}
            </Typography>

            <div className={classes.body}>
                <div className={classes.row}>
                    <div className={classes.field}>
                        <Input
                            label={locale.fields.name}
                            placeholder={locale.placeholders.name}
                            value={values.name}
                            setValue={v => setField('name', v)}
                            isError={Boolean(errors.name)}
                            errorText={errors.name}
                            disabled={isSaving || !canUpdate}
                            autoFocus
                        />
                    </div>
                </div>

                <div className={classes.row}>
                    <div className={classes.field}>
                        <Input
                            label={locale.fields.description}
                            placeholder={locale.placeholders.description}
                            value={values.description ?? ''}
                            setValue={v => setField('description', v)}
                            multiline
                            disabled={isSaving || !canUpdate}
                        />
                    </div>
                </div>

                <div className={classes.row}>
                    <div className={classes.field}>
                        <Input
                            label={locale.fields.endpoint}
                            placeholder={locale.placeholders.endpoint}
                            value={values.endpoint}
                            setValue={v => setField('endpoint', v)}
                            isError={Boolean(errors.endpoint)}
                            errorText={errors.endpoint}
                            disabled={isSaving || !canUpdate}
                        />
                    </div>
                </div>

                <div className={classes.row}>
                    <div className={classes.field}>
                        <Typography className={classes.fieldLabel}>
                            {locale.fields.requestMethod}
                        </Typography>
                        <div className={classes.pickerWrapper}>
                            <Picker
                                options={methodOptions}
                                value={values.request_method}
                                onChange={v => setField('request_method', v)}
                                disabled={isSaving || !canUpdate}
                                isSortingDisabled
                                renderValue={renderSingleValue}
                            />
                        </div>
                    </div>
                </div>

                <div className={classes.row}>
                    <div className={classes.field}>
                        <Typography className={classes.fieldLabel}>
                            {locale.fields.reasons}
                        </Typography>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={REASONS_ORDERED}
                            value={values.reasons}
                            onChange={(_, newValue) =>
                                setField('reasons', newValue)
                            }
                            groupBy={reasonToGroup}
                            getOptionLabel={reasonLabel}
                            getOptionSelected={(option, val) => option === val}
                            disabled={isSaving || !canUpdate}
                            className={
                                errors.reasons
                                    ? `${classes.reasonsAutocomplete} ${classes.reasonsAutocompleteError}`
                                    : classes.reasonsAutocomplete
                            }
                            classes={{
                                paper: classes.autocompletePaper,
                                listbox: classes.autocompleteListbox
                            }}
                            renderOption={(option, { selected }) => (
                                <div className={classes.optionRow}>
                                    <Checkbox
                                        icon={
                                            <CheckBoxOutlineBlankIcon fontSize="small" />
                                        }
                                        checkedIcon={
                                            <CheckBoxIcon fontSize="small" />
                                        }
                                        checked={selected}
                                        className={classes.optionCheckbox}
                                        disableRipple
                                    />
                                    <span className={classes.optionLabel}>
                                        {reasonLabel(option)}
                                    </span>
                                </div>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const tagProps = getTagProps({ index })
                                    return (
                                        <Chip
                                            {...tagProps}
                                            key={option}
                                            size="small"
                                            label={reasonLabel(option)}
                                            deleteIcon={
                                                <CloseIcon fontSize="small" />
                                            }
                                            className={classes.chip}
                                        />
                                    )
                                })
                            }
                            renderGroup={params => (
                                <li key={params.key}>
                                    <div className={classes.groupHeader}>
                                        {groupLabel(params.group)}
                                    </div>
                                    <ul className={classes.groupItems}>
                                        {params.children}
                                    </ul>
                                </li>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    placeholder={
                                        values.reasons.length === 0
                                            ? locale.placeholders.reasonsEmpty
                                            : undefined
                                    }
                                    error={Boolean(errors.reasons)}
                                />
                            )}
                            noOptionsText={locale.placeholders.reasonsEmpty}
                        />
                        {values.reasons.length > 0 && !errors.reasons && (
                            <div className={classes.reasonsCounter}>
                                {values.reasons.length}{' '}
                                {values.reasons.length === 1
                                    ? locale.fields.reasonSingular
                                    : locale.fields.reasonPlural}{' '}
                                · {REASONS_ORDERED.length}{' '}
                                {locale.fields.reasonsTotalSuffix}
                            </div>
                        )}
                        {errors.reasons && (
                            <div className={classes.fieldError}>
                                {errors.reasons}
                            </div>
                        )}
                    </div>
                </div>

                <div className={classes.enabledRow}>
                    <div className={classes.enabledLabel}>
                        <span className={classes.enabledTitle}>
                            {locale.fields.isEnabled}
                        </span>
                        <span className={classes.enabledHint}>
                            {locale.fields.isEnabledHint}
                        </span>
                    </div>
                    <CustomSwitch2
                        checked={Boolean(values.is_enabled)}
                        onChange={(_, checked) =>
                            setField('is_enabled', checked)
                        }
                        disabled={isSaving || !canUpdate}
                    />
                </div>

                <div className={classes.section}>
                    <HeadersEditor
                        value={headerRows}
                        onChange={setHeaderRows}
                        error={errors.headers}
                        disabled={isSaving || !canUpdate}
                        version={effectiveVersion}
                        onVersionChange={setVersion}
                        versions={versions}
                        latestVersion={latestVersion}
                    />
                </div>
            </div>

            <div className={classes.actions}>
                <Button reverted onClick={onClose} disabled={isSaving}>
                    {locale.cancel}
                </Button>
                <Button onClick={handleSave} disabled={isSaving || !canUpdate}>
                    {mode === 'edit'
                        ? locale.saveChanges
                        : locale.createWebhook}
                </Button>
            </div>
        </Modal>
    )
}

function sameReasons(a, b) {
    if (a.length !== b.length) return false
    const sa = [...a].sort()
    const sb = [...b].sort()
    for (let i = 0; i < sa.length; i++) {
        if (sa[i] !== sb[i]) return false
    }
    return true
}
